import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { DateTime } from "luxon";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { PatternFormat } from "react-number-format";
import { useAppDispatch, useAppSelector } from "../store";
import { FormaPagamento } from "../types/common";
import type { CartaoCredito } from "../types/assinatura";
import type { Plano } from "../types/plano";
import assinaturaApi from "../api/assinatura.api";
import planoApi from "../api/plano.api";
import BandeirasCartoes from "../components/BandeirasCartoes";
import ListItem from "../components/ListItem";
import Section from "../components/Section";
import SectionSkeleton from "../components/SectionSkeleton";
import useTitle from "../hooks/useTitle";
import { toCurrency } from "../helpers/toCurrency";
import enqueueSnackbar from "../helpers/enqueueSnackbar";
import formatPlanoNome from "../helpers/formatPlanoNome";
import cartaoCreditoSchema from "../schemas/cartaoCredito";
import {
  DEFAULT_PLANO_ID,
  PROPOSTA_PLANO_ID_KEY,
  PROPOSTA_VENDEDOR_ID_KEY,
} from "../helpers/constants";

type Values = CartaoCredito;

function BeneficiosPlanoSection({ plano }: { plano: Plano }) {
  return (
    <Section>
      <Typography variant="h6">Benefícios exclusivos</Typography>
      <List>
        {plano.permitirDependentes && plano.maximoDependentes > 0 && (
          <ListItem
            icon={<CheckCircleIcon color="primary" />}
            primary={`Até ${plano.maximoDependentes} dependentes`}
          />
        )}
        <ListItem
          primary="Descontos de até 80% em consultas"
          icon={<CheckCircleIcon color="primary" />}
        />
        <ListItem
          primary="Descontos de até 80% em exames"
          icon={<CheckCircleIcon color="primary" />}
        />
        {plano.telemedicina && (
          <ListItem
            primary="Telemedicina gratuita para qualquer dia e hora, sem agendamentos"
            icon={<CheckCircleIcon color="primary" />}
          />
        )}
      </List>
    </Section>
  );
}

export default function AssinarPage() {
  useTitle("Assine já");

  const theme = useTheme();
  const dispatch = useAppDispatch();

  const { cliente } = useAppSelector((state) => state.cliente);
  const { loading, plano } = useAppSelector((state) => state.planos);

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { isSubmitting, isSubmitSuccessful },
  } = useForm<Values>({
    resolver: yupResolver(cartaoCreditoSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });

  async function load(abortController?: AbortController) {
    try {
      if (loading) return;

      dispatch({ type: "SET_LOADING_PLANO", payload: true });

      const propostaPlanoId = localStorage.getItem(PROPOSTA_PLANO_ID_KEY);

      const planoId =
        propostaPlanoId && !isNaN(+propostaPlanoId)
          ? +propostaPlanoId
          : DEFAULT_PLANO_ID;

      const plano = await planoApi.findById(planoId, abortController);

      dispatch({ type: "SET_PLANO", payload: plano });
    } catch (error) {
      if (error?.code === "ERR_CANCELED") {
        return;
      }

      enqueueSnackbar(
        error?.message ||
          "Não foi possível carregar as informações do plano a contratar.",
        "error"
      );
    } finally {
      dispatch({ type: "SET_LOADING_PLANO", payload: false });
    }
  }

  async function submit(data: Values) {
    try {
      const validade = DateTime.fromFormat(data.validade, "MMyy")
        .startOf("month")
        .toISODate();

      const franquiaVendedorId = localStorage.getItem(PROPOSTA_VENDEDOR_ID_KEY);

      await assinaturaApi.create({
        planoId: plano.id,
        formaPagamento: FormaPagamento.CreditoRecorrencia,
        cartaoCredito: {
          numero: data.numero,
          validade: validade,
          cvv: data.cvv,
          titular: data.titular,
        },
        franquiaVendedorId: !!franquiaVendedorId
          ? Number(franquiaVendedorId)
          : null,
      });

      enqueueSnackbar(
        "Assinatura concluída com sucesso! Você será redirecionado em alguns instantes...",
        "success"
      );

      localStorage.removeItem(PROPOSTA_PLANO_ID_KEY);
      localStorage.removeItem(PROPOSTA_VENDEDOR_ID_KEY);

      setTimeout(() => {
        window.location.href = "/app/minha-conta";
      }, 3000);
    } catch (error) {
      enqueueSnackbar(
        error?.message || "Não foi possível concluir a sua assinatura.",
        "error"
      );

      setError("root", error?.message);
    }
  }

  useEffect(() => {
    const abortController = new AbortController();

    if (cliente && !plano && !loading) {
      load(abortController);
    }

    return () => {
      abortController.abort();
    };
  }, [cliente, plano]);

  if (cliente && (cliente.planoId !== 1 || cliente.tipoConta === "D")) {
    return <Navigate to="/app/minha-conta" />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexFlow: "column nowrap",
        alignItems: "center",
        gap: theme.spacing(2),
        height: "100%",
      }}
    >
      <Box sx={{ textAlign: "center", pb: 2 }}>
        <Typography variant="h4">Assine já</Typography>
        <Typography>
          Finalize a contratação para ter acesso imediato a todos os seus
          benefícios.
        </Typography>
      </Box>

      {loading || !cliente || !plano ? (
        <SectionSkeleton />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexFlow: "column nowrap",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: theme.spacing(2),
            width: "100%",
            [theme.breakpoints.up("lg")]: {
              flexFlow: "row wrap",
              justifyContent: "center",
              alignItems: "flex-start",
            },
          }}
        >
          <form onSubmit={handleSubmit(submit)}>
            <Section
              sx={{
                display: "flex",
                flexFlow: "column nowrap",
                gap: theme.spacing(2),
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6">
                  {formatPlanoNome(plano.nome)}
                </Typography>
                <Typography>
                  Por apenas {toCurrency(plano.valor)} ao mês
                </Typography>
              </Box>

              <Controller
                control={control}
                name="numero"
                render={({
                  field: { onBlur, value, name },
                  fieldState: { error },
                }) => (
                  <PatternFormat
                    type="text"
                    label="Número do cartão"
                    name={name}
                    value={value || ""}
                    onBlur={onBlur}
                    onValueChange={({ value }) =>
                      setValue(name, value, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      })
                    }
                    error={!!error}
                    helperText={!!error ? error.message : undefined}
                    placeholder="1111 2222 3333 4444"
                    format="#### #### #### ####"
                    mask="_"
                    valueIsNumericString
                    customInput={TextField}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                )}
              />

              <Controller
                control={control}
                name="titular"
                render={({
                  field: { onBlur, onChange, value, name },
                  fieldState: { error },
                }) => (
                  <TextField
                    type="text"
                    variant="outlined"
                    label="Nome impresso no cartão"
                    placeholder="Nome impresso no cartão"
                    name={name}
                    value={value || ""}
                    onBlur={onBlur}
                    onChange={onChange}
                    error={!!error}
                    helperText={!!error ? error.message : undefined}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ notched: true }}
                    fullWidth
                  />
                )}
              />

              <Box
                sx={{
                  display: "flex",
                  flexflow: "row wrap",
                  gap: theme.spacing(2),
                }}
              >
                <Controller
                  control={control}
                  name="validade"
                  render={({
                    field: { onBlur, value, name },
                    fieldState: { error },
                  }) => (
                    <PatternFormat
                      type="text"
                      label="Validade do cartão"
                      placeholder={`${DateTime.now().toFormat("LL / yy")}`}
                      name={name}
                      value={value || ""}
                      onBlur={onBlur}
                      onValueChange={({ value }) =>
                        setValue(name, value, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        })
                      }
                      error={!!error}
                      helperText={!!error ? error.message : undefined}
                      format="## / ##"
                      mask="_"
                      valueIsNumericString
                      customInput={TextField}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="cvv"
                  render={({
                    field: { onBlur, value, name },
                    fieldState: { error },
                  }) => (
                    <PatternFormat
                      type="text"
                      label="CVV (Código de segurança)"
                      placeholder="123"
                      name={name}
                      value={value || ""}
                      onBlur={onBlur}
                      onValueChange={({ value }) =>
                        setValue(name, value, {
                          shouldDirty: true,
                          shouldTouch: true,
                          shouldValidate: true,
                        })
                      }
                      error={!!error}
                      helperText={!!error ? error.message : undefined}
                      format="###"
                      mask="_"
                      valueIsNumericString
                      customInput={TextField}
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  )}
                />
              </Box>

              <Button
                variant="contained"
                size="large"
                type="submit"
                disabled={isSubmitting || isSubmitSuccessful}
              >
                Contratar
              </Button>

              <BandeirasCartoes imgHeight={56} />
            </Section>
          </form>

          <BeneficiosPlanoSection plano={plano} />
        </Box>
      )}
    </Box>
  );
}
